import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, Paperclip, RotateCcw, Send, X } from 'lucide-react';
import axios from 'axios';
import { API, QUICK_REPLIES, SupportMessage, SupportThread, TYPE_LABELS, flag, quickText } from './supportTypes';

// Right side of the support inbox: messages, ready answers, reply box.

interface Props { threadId: number; onChanged: () => void }
type Detail = SupportThread & { messages: SupportMessage[] };

const ASSET = 'https://api.lifeshare.app';

const SupportConversation: React.FC<Props> = ({ threadId, onChanged }) => {
    const [detail, setDetail] = useState<Detail | null>(null);
    const [text, setText] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const bottom = useRef<HTMLDivElement>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    const load = async () => {
        try {
            const r = await axios.get(`${API}/threads/${threadId}`);
            setDetail(r.data);
            onChanged();
        } catch (e: any) {
            setError(e.response?.data?.detail || 'Konuşma alınamadı');
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setDetail(null); setText(''); setFile(null); setError(''); load(); }, [threadId]);
    useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [detail?.messages.length]);

    const send = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        setError('');
        try {
            const form = new FormData();
            form.append('body', text.trim());
            if (file) form.append('file', file);
            await axios.post(`${API}/threads/${threadId}/reply`, form);
            setText('');
            setFile(null);
            await load();
        } catch (e: any) {
            setError(e.response?.data?.detail || 'Gönderilemedi');
        } finally {
            setSending(false);
        }
    };

    const setStatus = async (status: 'open' | 'resolved') => {
        await axios.post(`${API}/threads/${threadId}/status`, { status });
        await load();
    };

    if (!detail) {
        return <div className="flex-1 flex items-center justify-center text-sm text-gray-400">{error || 'Yükleniyor…'}</div>;
    }
    const u = detail.user;

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-start justify-between gap-3 flex-wrap p-4 border-b border-gray-100">
                <div className="min-w-0">
                    {detail.title && <p className="text-sm font-semibold text-purple-700">{detail.title}</p>}
                    <p className="font-semibold text-gray-900">{u.full_name || u.username} <span className="text-gray-400 font-normal">@{u.username}</span></p>
                    <p className="text-xs text-gray-500 break-words">
                        {flag(detail.language)} {detail.platform || '—'} {detail.os_version || ''} · {detail.app_version || '—'} · {detail.circles} çevre
                        {u.email ? ` · ${u.email}` : ' · e-postası yok (yalnız push)'}
                    </p>
                </div>
                {detail.status === 'open' ? (
                    <button onClick={() => setStatus('resolved')} className="min-h-[40px] px-4 py-2 rounded-xl bg-green-500 text-white text-sm flex items-center gap-1.5 hover:bg-green-600 shrink-0">
                        <CheckCircle className="w-4 h-4" /> Çözüldü
                    </button>
                ) : (
                    <button onClick={() => setStatus('open')} className="min-h-[40px] px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm flex items-center gap-1.5 hover:bg-gray-200 shrink-0">
                        <RotateCcw className="w-4 h-4" /> Yeniden aç
                    </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {detail.messages.map(m => {
                    const mine = m.sender === 'admin';
                    const type = m.feedback_type ? TYPE_LABELS[m.feedback_type] : null;
                    return (
                        <div key={m.id} className={`max-w-[85%] sm:max-w-lg p-3 rounded-2xl ${mine ? 'ml-auto bg-purple-600 text-white rounded-tr-sm' : 'bg-white shadow-sm rounded-tl-sm'}`}>
                            {!mine && (type || m.subject) && (
                                <p className="text-xs text-gray-400 mb-1">{type ? type[0] : ''}{m.subject ? ` · ${m.subject}` : ''}{m.rating ? ` · ${m.rating}/5` : ''}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                            {m.attachment_url && (
                                <img src={`${ASSET}${m.attachment_url}`} alt="" className="mt-2 rounded-xl max-h-60" />
                            )}
                            <p className={`text-xs mt-1 ${mine ? 'text-purple-200' : 'text-gray-400'}`}>
                                {new Date(m.created_at).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                {m.repeat_count > 0 && ` · ${m.repeat_count} kez daha gönderildi`}
                                {mine && ` · push ${m.delivered_push ? '✓' : '✗'}${u.email ? ` · e-posta ${m.delivered_email ? '✓' : '✗'}` : ''}`}
                            </p>
                        </div>
                    );
                })}
                <div ref={bottom} />
            </div>

            <div className="p-3 border-t border-gray-100 space-y-2" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
                <div className="flex gap-2 flex-wrap text-xs">
                    {QUICK_REPLIES.map(q => (
                        <button key={q.label} onClick={() => setText(quickText(q, detail.language))} className="px-3 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700">
                            {q.label}
                        </button>
                    ))}
                </div>
                {file && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                        <Paperclip className="w-3.5 h-3.5" /> {file.name}
                        <button onClick={() => setFile(null)}><X className="w-3.5 h-3.5" /></button>
                    </div>
                )}
                {error && <p className="text-xs text-red-600">{error}</p>}
                <div className="flex gap-2">
                    <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Cevap yaz…"
                        className="flex-1 min-w-0 px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-purple-500 resize-none" />
                    <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                        onChange={e => setFile(e.target.files?.[0] || null)} />
                    <button onClick={() => fileInput.current?.click()} className="px-3 min-w-[44px] rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center" title="Görsel ekle" aria-label="Görsel ekle">
                        <Paperclip className="w-5 h-5 text-gray-600" />
                    </button>
                    <button onClick={send} disabled={sending || !text.trim()} className="px-3 sm:px-5 min-w-[44px] rounded-xl bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-1.5" aria-label="Gönder">
                        <Send className="w-4 h-4" /> <span className="hidden sm:inline">{sending ? '…' : 'Gönder'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SupportConversation;
